import React from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  Card,
  CardContent,
  Button,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Videocam,
  Mic,
  Home,
  Light,
  Hearing,
  EmojiObjects,
  ChevronRight,
  SlowMotionVideo,
} from "@mui/icons-material";

const GOLD = "#D4AF37";

export default function SetupVideo() {
  const theme = useTheme();

  const tips = [
    {
      title: "Equipment can be easy",
      desc: "You don't need fancy gear. Most smartphones capture HD video. Focus on stability and decent lighting first.",
      icon: <Videocam />,
    },
    {
      title: "Students need to hear you",
      desc: "A good microphone is critical. Ensure it's 6-12 inches (15-30 cm) from you and free of background noise.",
      icon: <Mic />,
    },
    {
      title: "Make a studio",
      desc: "Find a quiet place with natural lighting. Simple backgrounds work best to keep students focused on you.",
      icon: <Home />,
    },
    {
      title: "Light the scene and your face",
      desc: "Turn off overhead lights. Use natural light or lamps in front of you to avoid shadows on your face.",
      icon: <Light />,
    },
    {
      title: "Reduce noise and echo",
      desc: "Turn off fans and record when it's quiet. Use rugs or furniture to dampen echo in your recording space.",
      icon: <Hearing />,
    },
  ];

  const requirements = [
    "Export in HD (at least 720p, preferably 1080p)",
    "Audio should come through both channels (stereo) and be synced",
    "Audio must be free of background noise and distracting echoes",
  ];

  const resources = [
    { title: "Teaching Center: Equipment", desc: "Build a home studio on a budget" },
    { title: "KaaS Trust & Safety", desc: "Policies for audio and video quality" },
    { title: "Join the community", desc: "Talk production tips with other instructors" },
  ];

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto" }}>
      <Typography variant="h4" fontWeight={900} gutterBottom>
        Setup & Test Video
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 6 }}>
        Get your audio and video right before you start recording. It's much easier to fix now than after you've filmed.
      </Typography>

      <Grid container spacing={6}>
        <Grid item xs={12} md={8}>
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" fontWeight={800} sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
              <EmojiObjects sx={{ color: GOLD }} />
              Production Tips
            </Typography>
            <Stack spacing={4}>
              {tips.map((tip, idx) => (
                <Box key={idx} sx={{ display: "flex", gap: 3 }}>
                  <Box 
                    sx={{ 
                      flexShrink: 0, 
                      width: 48, 
                      height: 48, 
                      borderRadius: "12px", 
                      bgcolor: alpha(GOLD, 0.1), 
                      color: GOLD, 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center" 
                    }}
                  >
                    {tip.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      {tip.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tip.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h6" fontWeight={800} gutterBottom>
              Technical Requirements
            </Typography>
            <List sx={{ bgcolor: alpha(GOLD, 0.02), borderRadius: 4, p: 2 }}>
              {requirements.map((req, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon sx={{ minWidth: 32, color: GOLD }}>
                    <ChevronRight fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={req} primaryTypographyProps={{ variant: "body2", fontWeight: 500 }} />
                </ListItem>
              ))}
            </List>
          </Box>

          <Box>
            <Typography variant="h6" fontWeight={800} gutterBottom>
              Helpful Resources
            </Typography>
            <Grid container spacing={3}>
              {resources.map((res, idx) => (
                <Grid item xs={12} sm={6} key={idx}>
                  <Link href="#" underline="none">
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        borderRadius: 3, 
                        transition: "0.2s", 
                        "&:hover": { borderColor: GOLD, bgcolor: alpha(GOLD, 0.05) } 
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={800} color="primary" gutterBottom>
                        {res.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {res.desc}
                      </Typography>
                    </Paper>
                  </Link>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card 
            sx={{ 
              borderRadius: 6, 
              textAlign: "center", 
              p: 2, 
              border: "1px solid", 
              borderColor: "divider", 
              boxShadow: "0 10px 40px rgba(0,0,0,0.05)",
              position: "sticky",
              top: 20
            }}
          >
            <CardContent>
              <Box sx={{ width: 120, height: 120, mx: "auto", mb: 2, bgcolor: alpha(GOLD, 0.1), borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <SlowMotionVideo sx={{ fontSize: 60, color: GOLD }} />
              </Box>
              <Typography variant="h6" fontWeight={800} gutterBottom>
                Free Video Feedback
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Get personalized advice on your audio and video from our experts.
              </Typography>
              <Button 
                variant="contained" 
                fullWidth 
                sx={{ 
                  borderRadius: "50px", 
                  bgcolor: GOLD, 
                  fontWeight: 800,
                  "&:hover": { bgcolor: alpha(GOLD, 0.8) }
                }}
              >
                Create a test video
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
